import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { CalendarRange, Gauge, Route, Package, TrendingUp, Info, ShieldPlus, ChevronLeft, ChevronRight, PackageX, Users, History } from 'lucide-react';
import LevelHistoryTable, { shortLabel } from '@/components/motivare/LevelHistoryTable';

const MONTHS = ['', 'Ianuarie', 'Februarie', 'Martie', 'Aprilie', 'Mai', 'Iunie', 'Iulie', 'August', 'Septembrie', 'Octombrie', 'Noiembrie', 'Decembrie'];
const lei = (v) => `${(v ?? 0).toLocaleString('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} lei`;
const qty = (v) => (Number.isInteger(v) ? v : (v ?? 0).toLocaleString('ro-RO', { maximumFractionDigits: 1 }));
const pct = (v) => (v == null ? '—' : `${Math.round(v * 1000) / 10}%`);
const fmtP = (p) => (p ? `${p.start.slice(8, 10)}.${p.start.slice(5, 7)} – ${p.end.slice(8, 10)}.${p.end.slice(5, 7)}` : '—');
const lvl = (l) => (l == null ? '—' : l === 0 ? 'ZERO' : `L${l}`);
const LEVEL_STYLE = { 0: 'bg-red-500', 1: 'bg-orange-500', 2: 'bg-amber-500', 3: 'bg-lime-500', 4: 'bg-emerald-500', 5: 'bg-green-600' };
const TYPE_LABEL = { hd: 'Livrări la domiciliu (HD)', ooh: 'Lockere (OOH)', none: 'Alte activități' };

const seenKey = (auth) => `motivare_seen_${auth?.user?.id || 'x'}`;
export const markMotivareSeen = (auth, importedAt) => { if (importedAt) localStorage.setItem(seenKey(auth), importedAt); };

export function useMotivareMe(auth, refreshKey) {
  const [me, setMe] = useState(null);
  useEffect(() => {
    if (!auth?.token) return;
    axios.get(`${API}/motivare/me`, { headers: { Authorization: `Bearer ${auth.token}` } })
      .then((r) => setMe(r.data)).catch(() => setMe({ visible: false }));
  }, [auth?.token, refreshKey]);
  const isNew = !!(me?.visible && me?.imported_at && localStorage.getItem(seenKey(auth)) !== me.imported_at);
  return { me, isNew };
}

function Row({ label, value, sub, testId }) {
  return (
    <div className="flex items-center justify-between py-2 border-b last:border-b-0 border-slate-100" data-testid={testId}>
      <span className="text-sm text-slate-700 pr-3">{label}</span>
      <div className="text-right shrink-0">
        <div className="text-sm font-semibold text-slate-900">{value}</div>
        {sub && <div className="text-xs text-slate-500">{sub}</div>}
      </div>
    </div>
  );
}

export default function MotivareMobile({ auth }) {
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState(null);
  const [awbOpen, setAwbOpen] = useState(false);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/motivare/me`, { headers: { Authorization: `Bearer ${auth.token}` }, params: sel || {} })
      .then((r) => { setMe(r.data); if (!sel) markMotivareSeen(auth, r.data?.imported_at); }).catch(() => setMe({ visible: false })).finally(() => setLoading(false));
  }, [auth, sel]);

  const months = me?.months || [];
  const idx = months.findIndex((m) => m.year === me?.year && m.month === me?.month);
  const go = (d) => { const t = months[idx + d]; if (t) setSel({ year: t.year, month: t.month }); };

  if (loading) return <div className="text-center text-slate-500 py-10" data-testid="motivare-mobile-loading">Se încarcă...</div>;
  if (!me?.visible) return <div className="text-center text-slate-500 py-10 px-6" data-testid="motivare-mobile-hidden">Momentan indisponibil.</div>;
  if (!me.has_data) return <div className="text-center text-slate-500 py-10 px-6" data-testid="motivare-mobile-nodata">Nu există încă o raportare pentru luna aceasta.</div>;

  const full = me.mode === 'full';
  const groups = ['hd', 'ooh', 'none'].map((t) => ({ type: t, items: me.items.filter((i) => i.level_type === t) })).filter((g) => g.items.length);
  const nextLevel = full && me.level != null && me.level < 5 ? me.level + 1 : null;
  const nextGain = nextLevel != null && me.level_coefficients ? Math.round(((me.parts?.hd || 0) * (me.level_coefficients[String(nextLevel)] - me.coefficient)) / 100 * 100) / 100 : null;

  return (
    <div className="space-y-3" data-testid="motivare-mobile">
      <div className="bg-white rounded-xl p-4 shadow-sm" data-testid="motivare-mobile-period">
        {me.history_enabled && months.length > 1 ? (
          <div className="flex items-center justify-between mb-2" data-testid="motivare-mobile-month-nav">
            <button type="button" onClick={() => go(1)} disabled={idx < 0 || idx >= months.length - 1} className="p-2 rounded-lg bg-slate-100 disabled:opacity-30 min-w-[40px]" data-testid="motivare-mobile-prev-month" aria-label="Luna anterioară"><ChevronLeft className="w-4 h-4 mx-auto" /></button>
            <div className="text-sm font-semibold text-slate-800 uppercase tracking-wide" data-testid="motivare-mobile-month-label">{MONTHS[me.month]} {me.year}</div>
            <button type="button" onClick={() => go(-1)} disabled={idx <= 0} className="p-2 rounded-lg bg-slate-100 disabled:opacity-30 min-w-[40px]" data-testid="motivare-mobile-next-month" aria-label="Luna următoare"><ChevronRight className="w-4 h-4 mx-auto" /></button>
          </div>
        ) : (
          <div className="text-xs uppercase tracking-wide text-slate-400 mb-1" data-testid="motivare-mobile-month-label">{MONTHS[me.month]} {me.year}</div>
        )}
        <div className="flex items-center gap-2 text-sm text-slate-700"><CalendarRange className="w-4 h-4 text-blue-600" /> Perioadă raportare: <b>{fmtP(me.activity_period)}</b></div>
        {me.level_period && !me.level_carried && <div className="flex items-center gap-2 text-sm text-slate-700 mt-1"><Gauge className="w-4 h-4 text-emerald-600" /> Level calculat pe: <b>{fmtP(me.level_period)}</b></div>}
        {me.level_carried && <div className="flex items-start gap-2 text-sm text-amber-700 bg-amber-50 rounded-lg px-3 py-2 mt-1" data-testid="motivare-mobile-level-pending"><Info className="w-4 h-4 shrink-0 mt-0.5" /> Level-ul pe luna aceasta nu se poate calcula încă — primul raport Level al lunii nu a sosit. Nivelul tău va apărea după primul raport.</div>}
        {me.is_past_month
          ? <div className="text-xs text-amber-700 mt-2 flex items-center gap-1" data-testid="motivare-mobile-past-month"><Info className="w-3.5 h-3.5" /> Lună încheiată — situația finală la ultima raportare din {MONTHS[me.month].toLowerCase()}.</div>
          : <div className="text-xs text-slate-400 mt-2">Situația nu este în timp real — se actualizează la fiecare raportare.</div>}
      </div>

      <div className="bg-white rounded-xl p-4 shadow-sm flex items-center gap-4" data-testid="motivare-mobile-level">
        <div className={`${me.level_carried || me.level == null ? 'bg-slate-300' : LEVEL_STYLE[me.level]} text-white rounded-xl w-16 h-16 flex items-center justify-center text-xl font-bold shrink-0`}>
          {me.level_carried ? '—' : me.level == null ? '—' : me.level === 0 ? 'ZERO' : `L${me.level}`}
        </div>
        <div className="flex-1">
          <div className="text-sm font-semibold text-slate-800">Level-ul tău</div>
          {me.level_carried ? (
            <div className="text-xs text-slate-500">În așteptarea primului raport Level al lunii.</div>
          ) : (
            <>
              <div className="text-xs text-slate-500">Livrări la timp (OTD): <b>{pct(me.otd)}</b></div>
              {me.backup && me.level != null && (
                <div className="text-xs text-violet-700 mt-1 flex items-start gap-1" data-testid="motivare-mobile-backup">
                  <ShieldPlus className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                  <span>Ești curier de back-up: primești un Level peste cel realizat (raport: <b>{lvl(me.level_raw)}</b> → aplicat: <b>{lvl(me.level)}</b>).</span>
                </div>
              )}
              {full && <div className="text-xs text-slate-500">Coeficient bonus HD: <b>{me.coefficient}%</b></div>}
              {nextGain != null && nextGain > 0 && (
                <div className="text-xs text-emerald-700 mt-1 flex items-center gap-1" data-testid="motivare-mobile-next-level"><TrendingUp className="w-3.5 h-3.5" /> La Level {nextLevel} ai avea +{lei(nextGain)}</div>
              )}
            </>
          )}
        </div>
      </div>

      {me.show_awb && (
        <div className="bg-white rounded-xl shadow-sm border border-red-100 overflow-hidden" data-testid="motivare-mobile-awb-card">
          <button type="button" onClick={() => setAwbOpen((o) => !o)} disabled={!me.nontime_count} className="w-full flex items-center gap-3 p-4 text-left disabled:cursor-default" data-testid="motivare-mobile-awb-toggle">
            <div className={`${me.nontime_count ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-400'} rounded-xl w-11 h-11 flex items-center justify-center shrink-0`}><PackageX className="w-5 h-5" /></div>
            <div className="flex-1">
              <div className="text-sm font-semibold text-slate-800">Colete întârziate (AWB)</div>
              <div className="text-xs text-slate-500">
                {me.nontime_count ? <><b className="text-red-700">{me.nontime_count} AWB</b> ți-au impactat Level-ul · apasă pentru listă</> : 'Niciun AWB întârziat în raportul Level curent'}
              </div>
            </div>
            {me.nontime_count > 0 && <ChevronRight className={`w-5 h-5 text-slate-400 transition-transform ${awbOpen ? 'rotate-90' : ''}`} />}
          </button>
          {awbOpen && me.nontime?.length > 0 && (
            <div className="px-4 pb-4" data-testid="motivare-mobile-awb-list">
              <div className="text-xs text-slate-600 mb-2">
                Acestea sunt AWB-urile care ți-au impactat Level-ul în <b>ultima zi de Level ({me.level_period ? `${me.level_period.end.slice(8, 10)}.${me.level_period.end.slice(5, 7)}` : ''})</b> — nu sunt toate întârzierile de la începutul raportării.
              </div>
              {Object.entries(me.nontime.reduce((g, i) => { (g[i.indicator || '—'] ||= []).push(i.awb); return g; }, {})).map(([ind, list]) => (
                <div key={ind} className="mb-2">
                  <div className="flex justify-between text-[11px] font-semibold text-slate-700 bg-slate-100 rounded px-2 py-1"><span>{ind}</span><span>{list.length}</span></div>
                  <ul className="mt-1">{list.map((a, i) => <li key={`${a}-${i}`} className="font-mono text-xs text-slate-700 px-2 py-0.5 border-b border-slate-100 last:border-0">{a}</li>)}</ul>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {me.level != null && me.kpi_history && (
        <div className="bg-white rounded-xl p-4 shadow-sm" data-testid="motivare-mobile-kpi-history">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-slate-400 mb-1"><History className="w-3.5 h-3.5" /> Istoric Level · indicatori pe zile</div>
          <div className="text-[11px] text-slate-500 mb-2">Vezi pe ce indicator pierzi și cum evoluezi de la o zi la alta (▲ mai bine / ▼ mai slab față de ziua anterioară).</div>
          <LevelHistoryTable data={me.kpi_history} compact />
          <div className="text-[10px] text-slate-400 mt-2 flex flex-wrap gap-x-2">
            {(me.kpi_history.kpi_order || []).map((c) => <span key={c.key}><b>{shortLabel(c.key, c.label)}</b> = {c.label}</span>)}
          </div>
        </div>
      )}

      {me.level != null && me.team_level_avg != null && (
        <div className="bg-white rounded-xl p-4 shadow-sm" data-testid="motivare-mobile-team">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-slate-400 mb-3"><Users className="w-3.5 h-3.5" /> Tu în echipă · Level</div>
          <div className="relative h-12 mx-2">
            <div className="absolute left-0 right-0 top-5 h-1.5 rounded-full bg-gradient-to-r from-red-400 via-amber-400 to-green-500" />
            {[0, 1, 2, 3, 4, 5].map((l) => (
              <div key={l} className="absolute top-3 -translate-x-1/2 text-[9px] text-slate-400" style={{ left: `${(l / 5) * 100}%`, top: '30px' }}>{l === 0 ? 'ZERO' : `L${l}`}</div>
            ))}
            <div className="absolute -translate-x-1/2 top-[13px] w-4 h-4 rounded-full border-2 border-slate-500 bg-white" style={{ left: `${(me.team_level_avg / 5) * 100}%` }} title="media echipei" data-testid="motivare-mobile-team-avg" />
            <div className={`absolute -translate-x-1/2 top-[11px] w-5 h-5 rounded-full ring-2 ring-white shadow ${LEVEL_STYLE[me.level]}`} style={{ left: `${(me.level / 5) * 100}%` }} data-testid="motivare-mobile-team-me" />
          </div>
          <div className="flex items-center justify-between text-xs mt-2">
            <span className="flex items-center gap-1"><span className={`inline-block w-2.5 h-2.5 rounded-full ${LEVEL_STYLE[me.level]}`} /> tu: <b>{lvl(me.level)}</b></span>
            <span className="flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-full border-2 border-slate-500 bg-white" /> media echipei: <b>L{me.team_level_avg}</b> ({me.team_count} curieri)</span>
          </div>
          <div className={`text-xs mt-1 font-medium ${me.level >= me.team_level_avg ? 'text-emerald-700' : 'text-amber-700'}`} data-testid="motivare-mobile-team-diff">
            {me.level >= me.team_level_avg ? `Ești cu ${(me.level - me.team_level_avg).toFixed(1)} peste media echipei` : `Ești cu ${(me.team_level_avg - me.level).toFixed(1)} sub media echipei`}
          </div>
        </div>
      )}

      {me.level_history?.length > 0 && (
        <div className="bg-white rounded-xl p-4 shadow-sm" data-testid="motivare-mobile-trend">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-slate-400"><TrendingUp className="w-3.5 h-3.5" /> Evoluția Level-ului</div>
            {(() => {
              const h = me.level_history.filter((x) => x.level != null);
              if (h.length < 2) return <span className="text-xs text-slate-400">prima lună</span>;
              const d = h[h.length - 1].level - h[h.length - 2].level;
              return <span className={`text-xs font-medium ${d > 0 ? 'text-emerald-700' : d < 0 ? 'text-red-600' : 'text-slate-500'}`} data-testid="motivare-mobile-trend-diff">{d > 0 ? '▲ în creștere' : d < 0 ? '▼ în scădere' : '= la fel'} față de luna trecută</span>;
            })()}
          </div>
          <svg viewBox="0 0 300 90" className="w-full h-24" data-testid="motivare-mobile-trend-chart">
            {[0, 1, 2, 3, 4, 5].map((l) => <line key={l} x1="28" x2="296" y1={8 + (5 - l) * 12} y2={8 + (5 - l) * 12} stroke="#e2e8f0" strokeWidth="1" />)}
            {[0, 5].map((l) => <text key={l} x="0" y={11 + (5 - l) * 12} fontSize="8" fill="#94a3b8">{l === 0 ? 'ZERO' : 'L5'}</text>)}
            {(() => {
              const h = me.level_history;
              const n = h.length;
              const x = (i) => (n === 1 ? 162 : 36 + (i * 252) / (n - 1));
              const y = (l) => 8 + (5 - l) * 12;
              const pts = h.map((p, i) => (p.level == null ? null : `${x(i)},${y(p.level)}`)).filter(Boolean).join(' ');
              return (
                <>
                  {n > 1 && <polyline points={pts} fill="none" stroke="#3b82f6" strokeWidth="2" />}
                  {h.map((p, i) => p.level != null && (
                    <g key={i}>
                      <circle cx={x(i)} cy={y(p.level)} r={i === n - 1 ? 5 : 3.5} fill={i === n - 1 ? '#1d4ed8' : '#60a5fa'} stroke="#fff" strokeWidth="1.5" />
                      <text x={x(i)} y="86" fontSize="8" textAnchor="middle" fill={i === n - 1 ? '#1e293b' : '#64748b'} fontWeight={i === n - 1 ? 700 : 400}>{MONTHS[p.month].slice(0, 3)}</text>
                    </g>
                  ))}
                </>
              );
            })()}
          </svg>
        </div>
      )}

      <div className="bg-white rounded-xl p-4 shadow-sm">
        <Row label="Zile lucrate" value={qty(me.days)} testId="motivare-mobile-days" />
        {me.km != null && <Row label={<span className="flex items-center gap-1"><Route className="w-3.5 h-3.5" /> Km parcurși</span>} value={`${qty(me.km)} km`} sub={full ? lei(me.km_amount) : null} testId="motivare-mobile-km" />}
      </div>

      {groups.map((g) => (
        <div key={g.type} className="bg-white rounded-xl p-4 shadow-sm" data-testid={`motivare-mobile-group-${g.type}`}>
          <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-slate-400 mb-2"><Package className="w-3.5 h-3.5" /> {TYPE_LABEL[g.type]}</div>
          {g.items.map((i) => <Row key={i.key} label={i.label} value={qty(i.qty)} sub={full ? lei(i.amount) : null} testId={`motivare-mobile-item-${i.key}`} />)}
          {full && g.type === 'hd' && (
            <div className="mt-3 pt-2 border-t border-slate-200 flex items-center justify-between text-sm" data-testid="motivare-mobile-bonus-hd">
              <span className="text-slate-600">Bonus HD ({lei(me.parts.hd)} × {me.coefficient}%)</span>
              <b>{lei(me.bonus_hd)}</b>
            </div>
          )}
        </div>
      ))}

      {full && (
        <div className="bg-slate-900 text-white rounded-xl p-4 shadow-sm" data-testid="motivare-mobile-total">
          <div className="text-xs uppercase tracking-wide text-slate-400">Bonus estimat</div>
          <div className="text-3xl font-bold mt-1">{me.eligible ? lei(me.total) : '—'}</div>
          {!me.eligible && <div className="text-xs text-amber-300 mt-1">Sub numărul minim de zile lucrate</div>}
          {me.deductions > 0 && <div className="text-xs text-red-300 mt-1">Include rețineri: −{lei(me.deductions)}</div>}
          <div className="text-xs text-slate-400 mt-2 flex items-start gap-1"><Info className="w-3.5 h-3.5 shrink-0 mt-0.5" /> Valoare estimativă la data raportării; rezultatul final se stabilește la închiderea lunii.</div>
        </div>
      )}
    </div>
  );
}
